import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  LayoutGrid,
  DollarSign,
  HardHat,
  CreditCard,
  Package,
  Send,
  ChevronRight,
  ExternalLink,
  Zap,
  HelpCircle,
  Settings,
  Loader2,
  CheckCircle,
} from 'lucide-react';

// Sample dashboard data for Overview (illustrative insights per module)
const OVERVIEW_DASHBOARD = {
  qbo: {
    title: 'QuickBooks Online',
    icon: DollarSign,
    color: '#2563eb',
    insight: 'AR aging and open payables',
    chartData: [
      { name: 'Current', value: 42, fill: '#93c5fd' },
      { name: '1–30 days', value: 28, fill: '#60a5fa' },
      { name: '31–60 days', value: 12, fill: '#3b82f6' },
      { name: '60+ days', value: 8, fill: '#2563eb' },
    ],
    metrics: [
      { label: 'Open AR', value: '$124,500' },
      { label: 'Bills due (30d)', value: '$31,200' },
    ],
  },
  fieldwire: {
    title: 'Fieldwire',
    icon: HardHat,
    color: '#059669',
    insight: 'Tasks and project health',
    chartData: [
      { name: 'Open', value: 34 },
      { name: 'In progress', value: 56 },
      { name: 'Done', value: 89 },
    ],
    metrics: [
      { label: 'Active projects', value: '12' },
      { label: 'Overdue tasks', value: '7' },
    ],
  },
  ramp: {
    title: 'Ramp',
    icon: CreditCard,
    color: '#7c3aed',
    insight: 'Spend by category',
    chartData: [
      { name: 'Subcontractors', value: 38 },
      { name: 'Materials', value: 28 },
      { name: 'Equipment', value: 18 },
      { name: 'Office', value: 10 },
      { name: 'Other', value: 6 },
    ],
    metrics: [
      { label: 'MTD spend', value: '$89,400' },
      { label: 'Pending approvals', value: '5' },
    ],
  },
  ontrack: {
    title: 'On-Track (Hilti)',
    icon: Package,
    color: '#ea580c',
    insight: 'Assets by status',
    chartData: [
      { name: 'In use', value: 72 },
      { name: 'Available', value: 31 },
      { name: 'In service', value: 8 },
    ],
    metrics: [
      { label: 'Total assets', value: '111' },
      { label: 'Due for service', value: '4' },
    ],
  },
};

const API_CONFIG_KEY = 'bcs_carolinas_api_config';

const defaultApiConfig = () => ({
  qbo: { realmId: '', accessToken: '', environment: 'sandbox' },
  fieldwire: { refreshToken: '', region: 'us' },
  ramp: { accessToken: '' },
  ontrack: { baseUrl: 'https://unite.ontrack3.hilti.com', bearerToken: '' },
});

// High-fidelity suggested questions per module (users can click to ask)
const SUGGESTED_QUESTIONS = {
  overview: [
    'Show me open invoices and unpaid bills across QuickBooks',
    'Which Fieldwire projects have tasks due this week?',
    'What are our top Ramp card spend categories this month?',
    'List assets currently at the Raleigh job site from On-Track',
  ],
  qbo: [
    'List all unpaid invoices and their due dates',
    'Show me accounts receivable aging summary',
    'Which customers have the highest outstanding balance?',
    'What bills are due in the next 30 days?',
    'Give me a summary of journal entries for last month',
    'List vendors we paid over $10k this quarter',
    'Show customer list with open balance and contact',
    'What are our top 5 expense accounts by amount?',
  ],
  fieldwire: [
    'Which tasks are overdue across all projects?',
    'Show me tasks assigned to me due this week',
    'List projects with the most open punch items',
    'What drawings were updated in the last 7 days?',
    'Which projects have RFIs still open?',
    'Show task completion rate by project',
    'List upcoming inspections or sign-offs',
    'What’s the status of submittals for Project X?',
  ],
  ramp: [
    'What’s our total card spend this month by category?',
    'Show pending card transactions awaiting approval',
    'List the top 10 merchants by spend this quarter',
    'Which cards have the highest utilization?',
    'Show reimbursements submitted but not yet paid',
    'What spend policies were triggered recently?',
    'Compare department spend vs. last month',
    'List receipts missing for card transactions',
  ],
  ontrack: [
    'List all assets at location Raleigh with status In Use',
    'Which assets are due for service or inspection?',
    'Show quantity items with low stock or below reorder point',
    'What transfers are in progress between locations?',
    'List assets assigned to employee John Smith',
    'Which assets have open service orders?',
    'Show quantity item allocations for project 25-480',
    'List documents linked to asset category Power Tools',
  ],
};

const CHAT_PLACEHOLDERS = {
  overview: 'Ask about QBO, Fieldwire, Ramp, or On-Track...',
  qbo: 'e.g. Unpaid invoices, AR aging, bills due...',
  fieldwire: 'e.g. Overdue tasks, RFIs, drawings updated...',
  ramp: 'e.g. Card spend, pending approvals, receipts...',
  ontrack: 'e.g. Assets by location, service due, transfers...',
};

const DEMO_RESPONSES = {
  overview: 'Connect QBO, Fieldwire, Ramp, and On-Track MCPs to get answers across all systems in one place.',
  qbo: 'With the QuickBooks MCP connected, you’d see live data from your company file (invoices, customers, vendors, etc.). Connect the Intuit QBO MCP in sandbox to try it.',
  fieldwire: 'With Fieldwire Analyst connected, you’d see tasks, projects, drawings, and RFIs. Deploy jobotics-fieldwire-analyst and connect it to your Fieldwire account.',
  ramp: 'With Ramp MCP connected, you’d see card spend, approvals, and policies. See Ramp’s developer docs to configure the MCP.',
  ontrack: 'With the ON!Track Unite API connected, you’d see assets, locations, quantity items, and transfers. Use the Unite API with your Hilti On-Track credentials.',
};

const TABS = [
  {
    id: 'overview',
    label: 'Overview',
    icon: LayoutGrid,
    description: 'One place for your tools',
  },
  {
    id: 'qbo',
    label: 'QuickBooks Online',
    icon: DollarSign,
    description: 'Accounting & invoicing',
  },
  {
    id: 'fieldwire',
    label: 'Fieldwire',
    icon: HardHat,
    description: 'Field & project management',
  },
  {
    id: 'ramp',
    label: 'Ramp',
    icon: CreditCard,
    description: 'Spend & cards',
  },
  {
    id: 'ontrack',
    label: 'On-Track (Hilti)',
    icon: Package,
    description: 'Assets & equipment',
  },
];

const CONTENT = {
  overview: {
    title: 'Overview',
    subtitle: '',
  },
  qbo: {
    title: 'QuickBooks Online',
    subtitle: 'Accounting & invoicing via MCP',
    body: (
      <>
        <p className="text-gray-700 mb-4">
          Intuit’s <strong>Model Context Protocol (MCP)</strong> lets AI apps securely access QuickBooks Online so you can query accounts, invoices, bills, customers, vendors, and more in natural language.
        </p>
        <ul className="list-disc list-inside text-gray-700 text-sm space-y-2 mb-4">
          <li>Accounts, Invoices, Bills, Bill Payments</li>
          <li>Customers, Vendors, Employees</li>
          <li>Estimates, Journal Entries, Purchases, Items</li>
        </ul>
        <a
          href="https://blogs.intuit.com/2025/10/21/experiment-with-intuits-local-mcp-server-early-preview/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          Intuit QBO MCP (early preview) <ExternalLink size={14} />
        </a>
      </>
    ),
  },
  fieldwire: {
    title: 'Fieldwire',
    subtitle: 'Field & project management',
    body: (
      <>
        <p className="text-gray-700 mb-4">
          Fieldwire powers field coordination, plans, and project tracking. The <strong>Jobotics Fieldwire Analyst</strong> integrates this data so you can ask about tasks, drawings, and progress in one place.
        </p>
        <p className="text-gray-600 text-sm mb-4">
          Repository: <code className="bg-gray-100 px-1 rounded">jobotics-fieldwire-analyst</code> (Jobotics-ai)
        </p>
        <a
          href="https://github.com/Jobotics-ai/jobotics-fieldwire-analyst"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 text-sm"
        >
          Fieldwire Analyst repo <ExternalLink size={14} />
        </a>
      </>
    ),
  },
  ramp: {
    title: 'Ramp',
    subtitle: 'Spend management & cards',
    body: (
      <>
        <p className="text-gray-700 mb-4">
          Ramp’s API and <strong>Ramp MCP</strong> expose spend, cards, and approvals so you can query transactions, manage policies, and reconcile without leaving your workflow.
        </p>
        <a
          href="https://docs.ramp.com/developer-api/v1/ramp-mcp"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
        >
          Ramp API / MCP docs <ExternalLink size={14} />
        </a>
      </>
    ),
  },
  ontrack: {
    title: 'On-Track (Hilti)',
    subtitle: 'Assets, equipment & locations',
    body: (
      <>
        <p className="text-gray-700 mb-4">
          <strong>ON!Track 3 – Unite API</strong> (Hilti) covers assets, locations, employees, documents, quantity items, transfers, and—for Pro—rentals. One integration for equipment and inventory visibility.
        </p>
        <ul className="list-disc list-inside text-gray-700 text-sm space-y-1 mb-4">
          <li>Assets, Asset Costs, Asset Services</li>
          <li>Documents, Employees (Workers), Groups, Locations</li>
          <li>Quantity Items, Allocations, Stock Modifications, Transfers</li>
          <li>Rental APIs (On!Track Pro)</li>
        </ul>
        <a
          href="https://unite.ontrack3.hilti.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
        >
          ON!Track Unite API <ExternalLink size={14} />
        </a>
      </>
    ),
  },
};

function hasModuleConfig(moduleId, config) {
  if (!config || moduleId === 'overview') return false;
  const c = config[moduleId] || {};
  if (moduleId === 'qbo') return !!(c.realmId && c.accessToken);
  if (moduleId === 'fieldwire') return !!c.refreshToken;
  if (moduleId === 'ramp') return !!c.accessToken;
  if (moduleId === 'ontrack') return !!c.bearerToken;
  return false;
}

function formatApiResult(result) {
  if (!result.success) return result.error || 'Request failed.';
  const parts = [result.summary || 'Done.'];
  if (result.data) {
    try {
      const str = typeof result.data === 'string' ? result.data : JSON.stringify(result.data);
      if (str.length <= 400) parts.push(str);
      else parts.push(str.slice(0, 400) + '…');
    } catch (_) {}
  }
  return parts.join('\n\n');
}

const BCSCarolinas = () => {
  const [activeTab, setActiveTabState] = useState('overview');
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [apiConfig, setApiConfigState] = useState(defaultApiConfig);
  const [configOpen, setConfigOpen] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(API_CONFIG_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setApiConfigState(prev => ({ ...defaultApiConfig(), ...prev, ...parsed }));
      }
    } catch (_) {}
  }, []);

  const setApiConfig = useCallback((moduleId, values) => {
    setApiConfigState(prev => {
      const next = { ...prev, [moduleId]: { ...(prev[moduleId] || {}), ...values } };
      try {
        localStorage.setItem(API_CONFIG_KEY, JSON.stringify(next));
      } catch (_) {}
      return next;
    });
  }, []);

  const content = CONTENT[activeTab] || CONTENT.overview;
  const isOverview = activeTab === 'overview';
  const suggestedForTab = SUGGESTED_QUESTIONS[activeTab] || [];
  const chatPlaceholder = CHAT_PLACEHOLDERS[activeTab] || CHAT_PLACEHOLDERS.overview;
  const configForTab = apiConfig[activeTab] || {};
  const hasConfig = hasModuleConfig(activeTab, apiConfig);

  const runQuery = useCallback(async (moduleId, message) => {
    const config = apiConfig[moduleId];
    if (!config || !hasModuleConfig(moduleId, apiConfig)) return null;
    const res = await fetch('/api/bcs-carolinas/query', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ module: moduleId, message, config }),
    });
    const data = await res.json().catch(() => ({}));
    return data;
  }, [apiConfig]);

  const handleSendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    const text = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', text }]);
    setChatInput('');

    if (hasConfig && activeTab !== 'overview') {
      setChatLoading(true);
      try {
        const result = await runQuery(activeTab, text);
        setChatMessages(prev => [...prev, { role: 'bot', text: result ? formatApiResult(result) : DEMO_RESPONSES[activeTab] }]);
      } catch (err) {
        setChatMessages(prev => [...prev, { role: 'bot', text: `Error: ${err.message || 'Request failed.'}` }]);
      }
      setChatLoading(false);
    } else {
      setChatMessages(prev => [...prev, { role: 'bot', text: DEMO_RESPONSES[activeTab] }]);
    }
  };

  const handleSuggestedQuestion = async (question) => {
    setChatMessages(prev => [...prev, { role: 'user', text: question }]);

    if (hasConfig && activeTab !== 'overview') {
      setChatLoading(true);
      try {
        const result = await runQuery(activeTab, question);
        setChatMessages(prev => [...prev, { role: 'bot', text: result ? formatApiResult(result) : DEMO_RESPONSES[activeTab] }]);
      } catch (err) {
        setChatMessages(prev => [...prev, { role: 'bot', text: `Error: ${err.message || 'Request failed.'}` }]);
      }
      setChatLoading(false);
    } else {
      setChatMessages(prev => [...prev, { role: 'bot', text: DEMO_RESPONSES[activeTab] }]);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Zap className="h-8 w-8 text-amber-500" />
          <h1 className="text-xl font-bold text-gray-900">Bartels Construction Solutions, LLC</h1>
        </div>
      </header>

      {/* Main: left tabs + right content + chat */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        {/* Left tabs */}
        <nav className="lg:w-64 flex-shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-gray-200 p-3 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-y-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTabState(t.id)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'text-gray-700 hover:bg-gray-100 border border-transparent'
                }`}
              >
                <Icon className={`h-5 w-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                <div className="min-w-0">
                  <div className="font-medium text-sm">{t.label}</div>
                  <div className="text-xs text-gray-500 truncate">{t.description}</div>
                </div>
                <ChevronRight className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
              </button>
            );
          })}
        </nav>

        {/* Right: description at top, main window = chat */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0">
          {/* Top: minimal module description */}
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <h2 className="text-base font-semibold text-gray-900">{content.title}</h2>
              {!isOverview && (
                <div className="w-full mt-2 border-t border-gray-200 pt-2">
                  <button
                    type="button"
                    onClick={() => setConfigOpen(prev => !prev)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-blue-600"
                  >
                    <Settings className="h-4 w-4" />
                    {hasConfig ? (
                      <span className="flex items-center gap-1"><CheckCircle className="h-4 w-4 text-green-600" /> API configured</span>
                    ) : (
                      <span>Configure API (required for live data)</span>
                    )}
                    <ChevronRight className={`h-4 w-4 transition-transform ${configOpen ? 'rotate-90' : ''}`} />
                  </button>
                  {configOpen && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                      {activeTab === 'qbo' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Realm ID (Company ID)</label>
                            <input
                              type="text"
                              value={configForTab.realmId || ''}
                              onChange={(e) => setApiConfig(activeTab, { realmId: e.target.value })}
                              placeholder="From QuickBooks URL or API"
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Access Token</label>
                            <input
                              type="password"
                              value={configForTab.accessToken || ''}
                              onChange={(e) => setApiConfig(activeTab, { accessToken: e.target.value })}
                              placeholder="OAuth2 access token from Intuit Developer"
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <p className="text-xs text-gray-500">Get tokens via <a href="https://developer.intuit.com" target="_blank" rel="noopener noreferrer" className="text-blue-600">Intuit Developer</a> or run the <a href="https://github.com/intuit/quickbooks-online-mcp-server" target="_blank" rel="noopener noreferrer" className="text-blue-600">QBO MCP server</a>.</p>
                        </>
                      )}
                      {activeTab === 'fieldwire' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Refresh Token (API Key)</label>
                            <input
                              type="password"
                              value={configForTab.refreshToken || ''}
                              onChange={(e) => setApiConfig(activeTab, { refreshToken: e.target.value })}
                              placeholder="From Fieldwire account settings"
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Region</label>
                            <select
                              value={configForTab.region || 'us'}
                              onChange={(e) => setApiConfig(activeTab, { region: e.target.value })}
                              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                            >
                              <option value="us">US</option>
                              <option value="eu">EU</option>
                            </select>
                          </div>
                          <p className="text-xs text-gray-500">See <a href="https://github.com/Jobotics-ai/jobotics-fieldwire-analyst" target="_blank" rel="noopener noreferrer" className="text-blue-600">Fieldwire Analyst</a> for token setup.</p>
                        </>
                      )}
                      {activeTab === 'ramp' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Access Token / API Key</label>
                            <input
                              type="password"
                              value={configForTab.accessToken || ''}
                              onChange={(e) => setApiConfig(activeTab, { accessToken: e.target.value })}
                              placeholder="From Ramp Developer dashboard"
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <p className="text-xs text-gray-500">See <a href="https://docs.ramp.com/developer-api/v1/ramp-mcp" target="_blank" rel="noopener noreferrer" className="text-blue-600">Ramp API docs</a>.</p>
                        </>
                      )}
                      {activeTab === 'ontrack' && (
                        <>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Base URL</label>
                            <input
                              type="text"
                              value={configForTab.baseUrl || 'https://unite.ontrack3.hilti.com'}
                              onChange={(e) => setApiConfig(activeTab, { baseUrl: e.target.value })}
                              placeholder="https://unite.ontrack3.hilti.com"
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Bearer Token</label>
                            <input
                              type="password"
                              value={configForTab.bearerToken || ''}
                              onChange={(e) => setApiConfig(activeTab, { bearerToken: e.target.value })}
                              placeholder="Hilti Cloud API Gateway token"
                              className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                            />
                          </div>
                          <p className="text-xs text-gray-500">See <a href="https://unite.ontrack3.hilti.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600">ON!Track Unite API</a>.</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Main: overview dashboard + chat */}
          <main className="flex-1 flex flex-col min-h-0 bg-gray-50 overflow-hidden">
            {isOverview && (
              <div className="flex-shrink-0 overflow-x-auto border-b border-gray-200 bg-white">
                <div className="p-3">
                  <p className="text-xs text-gray-500 mb-3">Sample insights — connect each module’s API for live data</p>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 min-w-[600px]">
                    {(['qbo', 'fieldwire', 'ramp', 'ontrack']).map((key) => {
                      const d = OVERVIEW_DASHBOARD[key];
                      const Icon = d.icon;
                      const COLORS = key === 'qbo' ? ['#93c5fd', '#60a5fa', '#3b82f6', '#2563eb'] : key === 'fieldwire' ? ['#6ee7b7', '#34d399', '#10b981'] : key === 'ramp' ? ['#c4b5fd', '#a78bfa', '#8b5cf6', '#7c3aed', '#6d28d9'] : ['#fdba74', '#fb923c', '#ea580c'];
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setActiveTabState(key)}
                          className="rounded-lg border border-gray-200 bg-gray-50/50 p-3 flex flex-col text-left hover:border-gray-300 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="h-4 w-4 text-gray-600" style={{ color: d.color }} />
                            <span className="font-semibold text-sm text-gray-900">{d.title}</span>
                          </div>
                          <p className="text-xs text-gray-500 mb-2">{d.insight}</p>
                          <div className="h-24 flex-shrink-0">
                            <ResponsiveContainer width="100%" height="100%">
                              {key === 'qbo' ? (
                                <PieChart>
                                  <Pie data={d.chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={28} outerRadius={40} paddingAngle={2}>
                                    {d.chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                  </Pie>
                                  <Tooltip formatter={(v) => [v, '']} />
                                </PieChart>
                              ) : (
                                <BarChart data={d.chartData} layout="vertical" margin={{ top: 0, right: 4, left: 0, bottom: 0 }}>
                                  <XAxis type="number" hide />
                                  <YAxis type="category" dataKey="name" width={52} tick={{ fontSize: 10 }} />
                                  <Tooltip formatter={(v) => [v, '']} />
                                  <Bar dataKey="value" fill={d.color} radius={2} />
                                </BarChart>
                              )}
                            </ResponsiveContainer>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {d.metrics.map((m, i) => (
                              <div key={i} className="text-xs">
                                <span className="text-gray-500">{m.label}:</span>{' '}
                                <span className="font-medium text-gray-900">{m.value}</span>
                              </div>
                            ))}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            {suggestedForTab.length > 0 && (
              <div className="px-3 py-2 bg-white border-b border-gray-100">
                <div className="flex flex-wrap gap-1.5">
                  {suggestedForTab.map((q, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSuggestedQuestion(q)}
                      className="text-xs px-2.5 py-1.5 rounded-md bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-800 transition-colors max-w-full truncate"
                      title={q}
                    >
                      {q.length > 48 ? q.slice(0, 48) + '…' : q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
              {chatMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="rounded-lg px-3 py-2 bg-gray-100 text-gray-600 text-sm flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Querying {activeTab === 'qbo' ? 'QuickBooks' : activeTab === 'fieldwire' ? 'Fieldwire' : activeTab === 'ramp' ? 'Ramp' : 'On-Track'}…
                  </div>
                </div>
              )}
            </div>
            <form onSubmit={handleSendChat} className="p-3 bg-white border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder={chatPlaceholder}
                  disabled={chatLoading}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
                />
                <button
                  type="submit"
                  disabled={chatLoading}
                  className="p-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                  aria-label="Send"
                >
                  {chatLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send size={18} />}
                </button>
              </div>
            </form>
          </main>
        </div>
      </div>
    </div>
  );
};

export default BCSCarolinas;
